import { apiService } from './apiService';
import { ReferralStats, ReferralLink, ReferralCode, Referral, ReferralTransaction } from '../types';

class ReferralService {
  private baseUrl = '/referrals';

  /**
   * Get referral statistics for the current user
   */
  async getReferralStats(): Promise<ReferralStats> {
    const response = await apiService.request<ReferralStats>(`${this.baseUrl}/stats/`);
    return response;
  }

  /**
   * Get referral link for the current user
   */
  async getReferralLink(): Promise<ReferralLink> {
    const response = await apiService.request<ReferralLink>(`${this.baseUrl}/link/`);
    return response;
  }

  /**
   * Validate a referral code
   */
  async validateReferralCode(code: string): Promise<{ valid: boolean; referrer_email?: string; error?: string }> {
    try {
      const response = await apiService.request<{ valid: boolean; referrer_email?: string; error?: string }>(`${this.baseUrl}/validate-code/`, {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      return response;
    } catch (error: any) {
      if (error.status === 400) {
        return { valid: false, error: error.message || 'Invalid referral code' };
      }
      throw error;
    }
  }

  /**
   * Get referral codes for the current user
   */
  async getReferralCodes(): Promise<ReferralCode[]> {
    const response = await apiService.request<ReferralCode[]>(`${this.baseUrl}/codes/`);
    return response;
  }

  /**
   * Get referrals made by the current user
   */
  async getReferrals(): Promise<Referral[]> {
    const response = await apiService.request<Referral[]>(`${this.baseUrl}/referrals/`);
    return response;
  }

  /**
   * Get referral transactions for the current user
   */
  async getReferralTransactions(): Promise<ReferralTransaction[]> {
    const response = await apiService.request<ReferralTransaction[]>(`${this.baseUrl}/transactions/`);
    return response;
  }

  /**
   * Create a referral code for the current user
   */
  async createReferralCode(): Promise<ReferralCode> {
    const response = await apiService.request<ReferralCode>(`${this.baseUrl}/codes/`, {
      method: 'POST',
      body: JSON.stringify({})
    });
    return response;
  }

  /**
   * Copy referral link to clipboard
   */
  async copyReferralLink(): Promise<boolean> {
    try {
      const referralLink = await this.getReferralLink();
      await navigator.clipboard.writeText(referralLink.referral_link);
      return true;
    } catch (error) {
      console.error('Failed to copy referral link:', error);
      return false;
    }
  }

  /**
   * Generate referral link with custom base URL
   */
  generateReferralLink(code: string, baseUrl?: string): string {
    const frontendUrl = baseUrl || window.location.origin;
    return `${frontendUrl}/register?ref=${code}`;
  }

  /**
   * Extract referral code from URL parameters
   */
  getReferralCodeFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ref');
  }
}

export const referralService = new ReferralService();
